﻿import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import StatCard from "@/components/base/StatCard";
import ConsumoChart from "./components/ConsumoChart";
import RecaudacionChart from "./components/RecaudacionChart";
import AlertasPanel from "./components/AlertasPanel";
import ActividadPanel from "./components/ActividadPanel";
import { useSession } from "@/auth/session";
import { getAprName } from "@/lib/runtime-config";
import {
  getDelinquencyReport,
  getExecutiveQuarterlyReport,
  getReadingsByPeriodReport,
  getSalesCollectionsReport,
  listNotifications,
  listOperations,
  type NotificationItem,
  type OperationItem,
} from "@/lib/api";

type DashboardData = {
  kpis: {
    clientesActivos: number;
    pagosPendientes: number;
    consumoPromedio: number;
    alertasActivas: number;
  };
  consumoMensual: Array<{ mes: string; consumo: number }>;
  recaudacion: {
    periodLabel: string;
    totalRecaudado: number;
    totalPendiente: number;
    totalVencido: number;
  };
  alertas: NotificationItem[];
  actividad: Array<{
    id: string;
    usuario: string;
    accion: string;
    tiempo: string;
    avatar: string;
  }>;
  periodo: {
    label: string;
    boletas: number;
    recaudado: number;
    cobranzaPct: number;
  };
};

const emptyDashboard: DashboardData = {
  kpis: {
    clientesActivos: 0,
    pagosPendientes: 0,
    consumoPromedio: 0,
    alertasActivas: 0,
  },
  consumoMensual: [],
  recaudacion: {
    periodLabel: formatMonthYear(new Date()),
    totalRecaudado: 0,
    totalPendiente: 0,
    totalVencido: 0,
  },
  alertas: [],
  actividad: [],
  periodo: {
    label: formatMonthYear(new Date()),
    boletas: 0,
    recaudado: 0,
    cobranzaPct: 0,
  },
};

export default function DashboardPage() {
  const aprName = getAprName() || "APR";
  const navigate = useNavigate();
  const { session, profile, hasModule } = useSession();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState<DashboardData>(emptyDashboard);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError("");
      try {
        const monthRange = getCurrentMonthRange();
        const [executive, sales, readings, delinquency, notifications, operations] = await Promise.all([
          getExecutiveQuarterlyReport(session),
          getSalesCollectionsReport(session, monthRange),
          getReadingsByPeriodReport(session, 12),
          getDelinquencyReport(session, 1),
          listNotifications(session, 20),
          hasModule("operaciones") ? listOperations(session) : Promise.resolve([]),
        ]);

        const consumoMensual = readings.rows
          .slice()
          .sort((a, b) => a.periodCode.localeCompare(b.periodCode))
          .map((row) => ({
            mes: toPeriodLabel(row.periodCode),
            consumo: row.totalConsumptionM3,
          }));

        const totalRecaudado = sales.collections.amount;
        const totalVencido = sales.receivables.overdueAmount;
        const totalPendiente = sales.receivables.currentAmount;

        const activity = buildActivity(operations);
        const unreadAlerts = notifications.items.filter((item) => !item.read);

        const next: DashboardData = {
          kpis: {
            clientesActivos: executive.kpis.activeCustomers,
            pagosPendientes: delinquency.summary.contracts,
            consumoPromedio: executive.kpis.avgQuarterConsumptionM3,
            alertasActivas: notifications.unread,
          },
          consumoMensual,
          recaudacion: {
            periodLabel: formatMonthYear(new Date()),
            totalRecaudado,
            totalPendiente,
            totalVencido,
          },
          alertas: (unreadAlerts.length > 0 ? unreadAlerts : notifications.items).slice(0, 6),
          actividad: activity,
          periodo: {
            label: formatMonthYear(new Date()),
            boletas: sales.sales.documents,
            recaudado: totalRecaudado,
            cobranzaPct: sales.collectionRatePct,
          },
        };

        if (!cancelled) {
          setData(next);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "No se pudo cargar el dashboard");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [session, hasModule]);

  const saludo = useMemo(() => {
    const fullName = profile?.fullName?.trim() ?? "";
    const firstName = fullName.split(" ").filter(Boolean)[0] ?? "Usuario";
    return firstName;
  }, [profile?.fullName]);

  const hoy = useMemo(() => {
    const dt = new Intl.DateTimeFormat("es-CL", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(new Date());
    return dt.charAt(0).toUpperCase() + dt.slice(1);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Bienvenido, {saludo}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{hoy} — {aprName}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/operaciones")}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-tools-line" /></div>
            Nueva OT
          </button>
          <button
            onClick={() => navigate("/facturacion")}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-add-line" /></div>
            Generar Boletas
          </button>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Clientes Activos"
          value={loading ? "..." : data.kpis.clientesActivos.toLocaleString("es-CL")}
          icon="ri-user-line"
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
          onClick={() => navigate("/clientes")}
        />
        <StatCard
          label="Pagos Pendientes"
          value={loading ? "..." : data.kpis.pagosPendientes.toLocaleString("es-CL")}
          icon="ri-time-line"
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
          onClick={() => navigate("/facturacion")}
        />
        <StatCard
          label="Consumo Promedio"
          value={loading ? "..." : `${data.kpis.consumoPromedio.toLocaleString("es-CL", { maximumFractionDigits: 1 })} m³`}
          icon="ri-drop-line"
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
        />
        <StatCard
          label="Alertas Activas"
          value={loading ? "..." : data.kpis.alertasActivas.toLocaleString("es-CL")}
          icon="ri-alarm-warning-line"
          iconBg="bg-red-50"
          iconColor="text-red-600"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3">
          <ConsumoChart data={data.consumoMensual} />
        </div>
        <div className="lg:col-span-2">
          <RecaudacionChart data={data.recaudacion} />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Registrar Pago", icon: "ri-money-dollar-circle-line", color: "text-emerald-600", bg: "bg-emerald-50", path: "/facturacion" },
          { label: "Operaciones", icon: "ri-tools-line", color: "text-blue-600", bg: "bg-blue-50", path: "/operaciones" },
          { label: "Nuevo Cliente", icon: "ri-user-add-line", color: "text-violet-600", bg: "bg-violet-50", path: "/clientes" },
          { label: "Informe Trimestral", icon: "ri-file-chart-line", color: "text-amber-600", bg: "bg-amber-50", path: "/reportes" },
        ].map((action) => (
          <button
            key={action.label}
            onClick={() => navigate(action.path)}
            className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer text-left"
          >
            <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${action.bg}`}>
              <i className={`${action.icon} text-lg ${action.color}`} />
            </div>
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">{action.label}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AlertasPanel items={data.alertas} />
        <ActividadPanel items={data.actividad} />
      </div>

      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-5 text-white">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-semibold text-lg">Período {data.periodo.label}</h3>
            <p className="text-blue-200 text-sm mt-0.5">Cierre disponible — {data.periodo.boletas.toLocaleString("es-CL")} boletas emitidas</p>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold">${data.periodo.recaudado.toLocaleString("es-CL")}</div>
              <div className="text-blue-200 text-xs">Recaudado</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{Math.round(data.periodo.cobranzaPct)}%</div>
              <div className="text-blue-200 text-xs">Cobranza</div>
            </div>
            <button
              onClick={() => navigate("/reportes")}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-chart-line" /></div>
              Ver Reportes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function buildActivity(operations: OperationItem[]): Array<{ id: string; usuario: string; accion: string; tiempo: string; avatar: string }> {
  return operations
    .slice()
    .sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime())
    .slice(0, 6)
    .map((operation) => {
      const usuario = operation.customerName || "Sistema APR";
      const statusLabel = mapOperationStatus(operation.status);
      const nombreOt = operation.operationTypeName || "operación";
      const contrato = operation.contractNumber || "sin contrato";
      return {
        id: operation.id,
        usuario,
        accion: `${statusLabel} OT de ${nombreOt} (${contrato})`,
        tiempo: toRelativeTime(operation.requestedAt),
        avatar: getInitials(usuario),
      };
    });
}

function mapOperationStatus(status: string): string {
  if (status === "completed") return "Completó";
  if (status === "in_progress") return "Inició";
  if (status === "assigned") return "Asignó";
  if (status === "reviewed") return "Revisó";
  return "Creó";
}

function getCurrentMonthRange(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    from: toIsoDate(from),
    to: toIsoDate(to),
  };
}

function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toPeriodLabel(periodCode: string): string {
  const [yearRaw, monthRaw] = periodCode.split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return periodCode;
  const dt = new Date(Date.UTC(year, month - 1, 1));
  return new Intl.DateTimeFormat("es-CL", { month: "short", year: "2-digit", timeZone: "UTC" }).format(dt);
}

function formatMonthYear(value: Date): string {
  return new Intl.DateTimeFormat("es-CL", { month: "long", year: "numeric" }).format(value);
}

function toRelativeTime(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.max(1, Math.round(diffMs / 60000));
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `Hace ${diffHour} h`;
  const diffDay = Math.round(diffHour / 24);
  return `Hace ${diffDay} d`;
}

function getInitials(fullName: string): string {
  const initials = fullName
    .split(" ")
    .map((value) => value.trim())
    .filter(Boolean)
    .slice(0, 2)
    .map((value) => value[0]?.toUpperCase() ?? "")
    .join("");
  return initials || "SA";
}
